<template>
  <div>
    <div class="video-bg">
      <div class="video-overlay-container">
        <div class="video-overlay-text">
          <span class="video-overlay-text-title">{{ t('homePageTextTitle1') }}</span>
          <span class="video-overlay-text-title">{{ t('homePageTextTitle2') }}</span>
          <span class="video-overlay-text-title">{{ t('homePageTextTitle3') }}</span>
        </div>
        <p class="video-overlay-text-description" v-html="t('homePageTextDescription')" />
        <NuxtLink to="/game"
          ><button class="video-overlay-join-btn">
            <span class="video-overlay-join-btn-text">
              {{ t('homePageJoinButton') }}
            </span>
          </button>
        </NuxtLink>
        <div class="video-overlay-btns-container">
          <div class="anchor-btn" @click="scrollToSection(HomePageSections.CRAFTING)">
            <img src="~/public/img/homePage/crafting.webp" alt="" />
          </div>
          <div class="anchor-btn" @click="scrollToSection(HomePageSections.SEASON)">
            <img src="~/public/img/homePage/season.webp" alt="" />
          </div>
          <div class="anchor-btn" @click="scrollToSection(HomePageSections.FARMING)">
            <img src="~/public/img/homePage/farming.webp" alt="" />
          </div>
          <div class="anchor-btn" @click="scrollToSection(HomePageSections.REFERRAL)">
            <img src="~/public/img/homePage/referral.webp" alt="" />
          </div>
        </div>
      </div>
      <div v-if="$device.isDesktop" class="video-overlay-gradient" />

      <video
        v-if="$device.isDesktop"
        ref="refVideo"
        muted
        loop
        playsinline
        poster="~public/img/video-poster.png"
        preload="auto"
      >
        <source v-if="$device.isDesktop" src="~public/assets/video/desktop-bg.mp4" type="video/mp4" />
        <source v-else src="~public/assets/video/mobile-bg.mp4" type="video/mp4" />
      </video>

      <div v-if="$device.isMobileOrTablet" class="wrapper-mobile"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '#imports';
import { HomePageSections } from '~/utils/constants/home';

const refVideo = ref<HTMLVideoElement>();
const { t } = useI18n();
const { isMobile } = useDevice();

const props = defineProps<{
  assetsLoaded: boolean;
}>();

const playVideo = async (videoElement: HTMLVideoElement) => {
  try {
    if (videoElement.paused) {
      await videoElement.play();
    }
  } catch (error) {
    console.error('Video playback error:', error);
  }
};

watch(
  [() => props.assetsLoaded, refVideo],
  ([assetsLoadedValue, videoEl]) => {
    // make sure that video & assets is loaded
    if (assetsLoadedValue && videoEl) {
      // check readyState, 4 means HAVE_ENOUGH_DATA, video prepared to play
      if (videoEl.readyState >= 4) {
        playVideo(videoEl);
      } else {
        // if video doesn't ready yet - add event listener once time
        // listener will work when browser make sure that he can play video without pauses
        videoEl.addEventListener('canplaythrough', () => playVideo(videoEl), { once: true });
      }
    }
  },
  { immediate: true }
);

const scrollToSection = (id: string) => {
  const el = document.getElementById(id);
  if (el) {
    if (isMobile) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      scrollTo({ top: el.offsetTop - 30, behavior: 'smooth' });
    }
  }
};
</script>

<style lang="scss" scoped>
.video-bg {
  display: flex;
  position: relative;
  height: calc(100vh - 150px);

  @media screen and (max-width: 560px) {
    height: calc(100svh - 86px);
  }

  video {
    height: 100%;
    width: 100%;
    overflow: hidden;
    object-fit: cover;

    @media screen and (max-width: 560px) {
      object-fit: contain;
    }
  }

  .video-overlay-gradient {
    position: absolute;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(5, 36, 48, 0.44723827030812324) 45%, rgba(17, 68, 88, 1) 100%);
  }

  .video-overlay-container {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    position: absolute;
    z-index: 3;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    padding: 20px;
  }

  .video-overlay-text {
    font-family: Grenze, sans-serif;
    font-size: 64px;
    font-weight: 700;
    line-height: 60px;
    text-wrap: nowrap;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    white-space: nowrap;
    color: #ffffff;

    @media screen and (max-width: 767px) {
      font-family: Grenze;
      font-size: 32px;
      line-height: 1;
      margin-bottom: 34px;
    }
  }

  .video-overlay-text-description {
    font-family: Grenze, sans-serif;
    font-weight: 400;
    font-size: 32px;
    line-height: 130%;
    letter-spacing: 0%;
    text-align: center;
    vertical-align: middle;
    color: #fff;

    @media screen and (max-width: 767px) {
      font-family: Eczar;
      font-weight: 500;
      font-size: 18px;
      line-height: 130%;
      letter-spacing: 0%;
      text-align: center;
      margin-bottom: 34px;
    }
  }

  .video-overlay-join-btn {
    min-width: 240px;
    min-height: 62px;

    border-radius: 32px;
    opacity: 1;
    padding-top: 12px;
    padding-right: 40px;
    padding-bottom: 14px;
    padding-left: 40px;
    background: linear-gradient(181.35deg, #fec941 0.97%, #fa5c03 98.67%);
    border: 2px solid #983700bf;

    font-family: Grenze, sans-serif;
    font-weight: 700;
    font-style: Bold;
    font-size: 28px;
    text-align: center;
    text-shadow: 0px 1px 0px #a14918;
    -webkit-text-stroke: 1px #351600cc;
    text-stroke: 1px #351600cc;

    transition: 0.5s ease;

    @media screen and (max-width: 479px) {
      margin-bottom: 80px;
    }
  }

  .video-overlay-join-btn:hover {
    background: #fec941;
    border: none;
  }

  .video-overlay-join-btn-text {
    display: inline-block;
    transition: transform 0.5s ease;
  }

  .video-overlay-join-btn:hover .video-overlay-join-btn-text {
    transform: scale(1.1);
  }

  .video-overlay-join-btn:active {
    color: #a14918;
  }

  .video-overlay-btns-container {
    display: flex;
    gap: 62px;

    @media screen and (max-width: 479px) {
      gap: 23px;
    }
  }

  .anchor-btn {
    width: 130px;
    height: 130px;
    background-size: cover;
    background-position: center;
    transition: background-image 0.3s ease;
    border-radius: 50%;
    border: 3px solid #2d6062;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #00000052;
    cursor: pointer;

    img {
      width: 80%;
      transition: background-image 0.3s ease;
    }

    @media (hover: hover) {
      &:hover {
        background-color: #ffffff1a;
        border: none;
        img {
          width: 90%;
        }
      }
    }

    @media screen and (max-width: 767px) {
      width: 65px;
      height: 65px;
    }
  }

  .wrapper-mobile {
    height: calc(100svh - 86px);
    width: 100%;
    overflow: hidden;
    object-fit: contain;
    padding: 0 23px;
    background-image: url('/img/homePage/hero-mobile.png');

    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
  }
}
</style>
